import { inject } from "@angular/core";
import { CanActivateFn, Router, Routes } from "@angular/router";
import { authGuard, vendorGuard } from "b2b-ecommerce-lib";
import { authGuardDialog } from "./core/guards/auth-dialog.guard";
import { SessionService } from "./services/login.service";

export const sellerRequestGuard: CanActivateFn = (route, state) => {
  const sessionService = inject(SessionService);
  const router = inject(Router);

  const user = sessionService.user();

  if (!user || user.sellerRequestId !== 1) {
    router.navigate(["/home"]);
    return false;
  }

  return true;
};

export const routes: Routes = [
  { path: "", redirectTo: "/home", pathMatch: "full" },
  {
    path: "home",
    loadComponent: () => import("./views/homepage/homepage.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "privacy",
    loadComponent: () => import("./views/privacy/privacy.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "terms-and-conditions",
    loadComponent: () => import("./views/terms-and-conditions/terms-and-conditions.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "account",
    loadComponent: () => import("./views/account/account.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/personal",
    loadComponent: () => import("./views/account/personal/personal.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/address",
    loadComponent: () => import("./views/account/my-addresses/my-addresses.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/orders",
    loadComponent: () => import("./views/account/my-orders/my-orders.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/orders/orders-details",
    loadComponent: () =>
      import("./views/account/my-orders/my-orders-details/my-orders-details.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/refund",
    loadComponent: () => import("./views/account/my-refunds/my-refunds.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/products",
    loadComponent: () => import("./views/account/my-products/my-products.component"),
    data: { requiresAuthentication: false },
    canActivate: [vendorGuard],
  },
  {
    path: "account/courses",
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
    children: [
      {
        path: "",
        loadComponent: () => import("./views/account/my-courses/my-courses.component"),
        data: { requiresAuthentication: false },
      },
      {
        path: "course/:title",
        loadComponent: () => import("./views/account/my-courses/view-course/view-course.component"),
        data: { requiresAuthentication: false },
      },
    ],
  },
  {
    path: "account/backoffice",
    loadComponent: () => import("./views/homepage/homepage.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/wishlist",
    loadComponent: () => import("./views/products/wishlist/wishlist.component"),
    data: { requiresAuthentication: false },
    canActivate: [authGuard],
  },
  {
    path: "account/seller/orders",

    data: { requiresAuthentication: false },
    canActivate: [vendorGuard],
    children: [
      {
        path: "",
        loadComponent: () => import("./views/account/seller-orders/seller-orders.component"),
        data: { requiresAuthentication: false },
      },
      {
        path: ":uuidOrdine/history",
        loadComponent: () =>
          import(
            "./views/account/seller-orders/components/seller-order-history/seller-order-history.component"
          ),
        data: { requiresAuthentication: false },
      },
      {
        path: "orders-details",
        loadComponent: () =>
          import("./views/account/my-orders/my-orders-details/my-orders-details.component"),
        data: { requiresAuthentication: false },
        canActivate: [vendorGuard],
      },
    ],
  },
  {
    path: "account/price-lists",
    data: { requiresAuthentication: false },
    canActivate: [vendorGuard],
    children: [
      {
        path: "",
        loadComponent: () => import("./views/account/my-price-lists/my-price-lists.component"),
        data: { requiresAuthentication: false },
      },
      {
        path: "new-price-list/:idSeller",
        loadComponent: () =>
          import(
            "./views/account/my-price-lists/components/save-price-lists/save-price-lists.component"
          ),
        data: { requiresAuthentication: false },
      },
      {
        path: "edit-price-list/:idSeller/:idPriceList",
        loadComponent: () =>
          import(
            "./views/account/my-price-lists/components/save-price-lists/save-price-lists.component"
          ),
        data: { requiresAuthentication: false },
      },
    ],
  },
  {
    path: "login",
    loadComponent: () => import("./views/login/login.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "login/:logout",
    loadComponent: () => import("./views/login/login.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "forgot-password",
    loadComponent: () => import("./views/login/forgot-password/forgot-password.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "password-reset",
    loadComponent: () => import("./views/login/set-password/set-password.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "registration",
    loadComponent: () => import("./views/registration/registration.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "registration/:confirm",
    loadComponent: () => import("./views/registration/registration.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "cart",
    loadComponent: () => import("./views/cart/cart.component"),
    canActivate: [authGuardDialog],
  },
  {
    path: "checkout",
    loadComponent: () => import("./views/checkout/checkout.component"),
    canActivate: [authGuardDialog],
  },
  {
    path: "checkout/:id",
    loadComponent: () => import("./views/checkout/checkout.component"),
    canActivate: [authGuardDialog],
  },
  {
    path: "completed-order/:orderNumber",
    loadComponent: () =>
      import("./views/checkout/components/order-details/order-details.component"),
    canActivate: [authGuard],
  },
  {
    path: "products",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "product-detail/:idCategory/:id",
    loadComponent: () => import("./views/products/product-detail/product-detail.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "products/:filter",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "products/:filter/:page/:size/:idsottocategoria",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "category/:idCategory",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "category/:idCategory/:page/:size/:idsottocategoria",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "category/:idCategory/:filter",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "category/:idCategory/:filter/:page/:size/:idsottocategoria",
    loadComponent: () => import("./views/products/products-list/products-list.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "account/products/add-product/:book",
    loadComponent: () =>
      import(
        "./views/account/my-products/components/prodotto-nuovo-modifica/prodotto-nuovo-modifica.component"
      ),
    canActivate: [vendorGuard],
  },
  {
    path: "account/products/edit/:idarticolo",
    loadComponent: () =>
      import(
        "./views/account/my-products/components/prodotto-nuovo-modifica/prodotto-nuovo-modifica.component"
      ),
    canActivate: [vendorGuard],
  },
  {
    path: "review-form/:idItem",
    loadComponent: () => import("./views/reviews/review-form/review-form.component"),
    canActivate: [authGuard],
  },
  {
    path: "info",
    loadComponent: () => import("./views/information/information.component"),
  },
  {
    path: "info/:topic",
    loadComponent: () => import("./views/information/information.component"),
  },
  {
    path: "loan",
    loadComponent: () => import("./views/loan-appennino/loan-appennino.component"),
  },
  {
    path: "info-be-seller",
    loadComponent: () => import("./views/seller-request/be-seller/be-seller.component"),
    canActivate: [authGuardDialog],
  },
  {
    path: "request-be-seller",
    loadComponent: () =>
      import("./views/seller-request/request-be-seller/request-be-seller.component"),
    canActivate: [sellerRequestGuard],
  },
  {
    path: "contacts",
    loadComponent: () => import("./views/contacts/contacts.component"),
    data: { requiresAuthentication: false },
  },
  {
    path: "footer-info",
    loadComponent: () => import("./views/footer-info/footer-info.component"),
    data: { requiresAuthentication: false },
    children: [
      {
        path: "",
        redirectTo: "aiuto-servizio-clienti",
        pathMatch: "full",
      },
      {
        path: "aiuto-servizio-clienti",
        loadComponent: () =>
          import(
            "./views/footer-info/components/aiuto-servizio-clienti/aiuto-servizio-clienti.component"
          ),
      },
      {
        path: "condizioni-generali",
        loadComponent: () =>
          import(
            "./views/footer-info/components/condizioni-generali/condizioni-generali.component"
          ),
      },
      {
        path: "condizioni-generali-utenti",
        loadComponent: () =>
          import("./views/footer-info/components/termini-generali/termini-generali.component"),
      },
      {
        path: "condizioni-generali-autori",
        loadComponent: () =>
          import("./views/footer-info/components/termini-generali/termini-generali.component"),
      },
      {
        path: "spedizioni-resi",
        loadComponent: () =>
          import("./views/footer-info/components/termini-generali/termini-generali.component"),
      },
      {
        path: "informativa-privacy",
        loadComponent: () => import("./views/footer-info/components/altro/altro.component"),
      },
    ],
  },
  {
    path: "**",
    loadComponent: () => import("./views/page-not-found/page-not-found.component"),
  },
];
